import React from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { PodcastEpisode, EpisodeStatus } from '@/types/episode.types';
import { Clip } from '@/types/clip.types';
import { format } from 'date-fns';
import {
  RefreshCw,
  AlertCircle,
  Loader2,
  Plus,
  CheckCircle,
  FileAudio,
  Play,
  Square,
  Info,
} from 'lucide-react';
import { AudioPlayer, AudioPlayerHandle } from '@/components/AudioPlayer';
import { StatusBadge } from '@/components/StatusBadge';
import { MergingAnimation } from '@/components/MergingAnimation';
import { PublishingAnimation } from '@/components/PublishingAnimation';
import { ScriptGenerationAnimation } from '@/components/ScriptGenerationAnimation';
import { AudioGenerationAnimation } from '@/components/AudioGenerationAnimation';
import { ClipCard } from '@/components/ClipCard';
import { MergePreviewItem } from '@/components/MergePreviewItem';
import { PublishedClipsList } from '@/components/PublishedClipsList';
import { motion, AnimatePresence } from 'framer-motion';
import { buildMergeSequence, formatTotalDuration, toDate } from '@/utils/episode-helpers';

export interface EpisodeStatusContentProps {
  episode: PodcastEpisode;
  clips: Clip[];
  clipsLoading: boolean;

  // Actions
  actionInProgress: string | null;
  refreshEpisode: () => void;
  loadClips: () => void;
  handleMoveClip: (clipId: string, direction: 'up' | 'down') => Promise<void>;
  handleDeleteClip: (clipId: string) => Promise<void>;
  handleRegenerateScript: (clipId: string) => Promise<void>;
  handleRegenerateAudio: (clipId: string) => Promise<void>;
  saveScriptForClip: (clipId: string, script: string) => Promise<void>;
  onAddArticles: () => void;

  // Playback
  currentlyPlayingId: string | null;
  isPlayingAll: boolean;
  playAllIndex: number | null;
  isPlayAllPaused: boolean;
  isPlayingAllClips: boolean;
  playAllClipsIndex: number | null;
  isPlayAllClipsPaused: boolean;
  isPlayingMerged: boolean;
  setIsPlayingMerged: (playing: boolean) => void;
  handlePlayAll: () => void;
  handleStopPlayAll: () => void;
  handlePlayStateChange: (id: string, playing: boolean) => void;
  handleSegmentEnded: (sequenceIndex: number) => void;
  handleClipEnded: (clipIndex: number) => void;

  // Merged audio
  mergedAudioUrl: string | null;
  mergedAudioLoading: boolean;
  mergedAudioError: string | null;
  mergedAudioRef: React.RefObject<AudioPlayerHandle | null>;
  totalDuration: number;

  // Lock
  isLockedByOther: boolean;
}

export const EpisodeStatusContent: React.FC<EpisodeStatusContentProps> = (props) => {
  const { episode, clips } = props;

  // Derived state
  const allScriptsReady = clips.length > 0 && clips.every((c) =>
    ['script_ready', 'script_approved', 'audio_queued', 'generating_audio', 'audio_ready', 'audio_approved'].includes(c.status)
  );
  const allAudioReady = clips.length > 0 && clips.every((c) =>
    ['audio_ready', 'audio_approved'].includes(c.status)
  );

  switch (episode.status) {
    case EpisodeStatus.DRAFT:
      return <DraftContent {...props} />;
    case EpisodeStatus.GENERATING_SCRIPTS:
      return allScriptsReady ? <ClipList {...props} /> : <GeneratingScriptsContent {...props} />;
    case EpisodeStatus.SCRIPTS_PENDING_REVIEW:
    case EpisodeStatus.SCRIPTS_APPROVED:
    case EpisodeStatus.AUDIO_PENDING_REVIEW:
      return <ClipList {...props} />;
    case EpisodeStatus.GENERATING_AUDIO:
      return allAudioReady ? <ClipList {...props} /> : <GeneratingAudioContent {...props} />;
    case EpisodeStatus.AUDIO_APPROVED:
      return <AudioApprovedContent {...props} />;
    case EpisodeStatus.MERGING:
      return <MergingContent />;
    case EpisodeStatus.MERGED:
      return <MergedContent {...props} />;
    case EpisodeStatus.PUBLISHING:
      return <PublishingContent />;
    case EpisodeStatus.PUBLISHED:
      return <PublishedContent {...props} />;
    case EpisodeStatus.PUBLISHING_FAILED:
      return <PublishingFailedContent {...props} />;
    default:
      return <DraftContent {...props} />;
  }
};

// ─────────────────────────────────────────────────────────────
// Shared components
// ─────────────────────────────────────────────────────────────

const HeaderRow: React.FC<{
  label: string;
  count?: number;
  progress?: string;
  actionButton?: React.ReactNode;
  showRefresh?: boolean;
  onRefresh: () => void;
  actionInProgress: string | null;
}> = ({ label, count, progress, actionButton, showRefresh = true, onRefresh, actionInProgress }) => (
  <div className="flex items-center justify-between mb-4 pb-4 border-b">
    <div className="flex items-center gap-3">
      <span className="font-semibold text-lg">{label}</span>
      {count !== undefined && <Badge variant="secondary">{count}</Badge>}
      {progress && (
        <>
          <span className="text-muted-foreground">·</span>
          <span className="text-sm text-muted-foreground">{progress}</span>
        </>
      )}
    </div>
    <div className="flex items-center gap-2">
      {actionButton}
      {showRefresh && (
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={!!actionInProgress}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      )}
    </div>
  </div>
);

const ClipList: React.FC<EpisodeStatusContentProps> = ({
  episode, clips,
  handleMoveClip, handleDeleteClip, handleRegenerateScript, handleRegenerateAudio, saveScriptForClip,
  currentlyPlayingId, isPlayingAllClips, playAllClipsIndex, isPlayAllClipsPaused,
  handlePlayStateChange, handleClipEnded, actionInProgress, isLockedByOther,
}) => (
  <div className="space-y-4">
    <AnimatePresence>
      {clips.map((clip, index) => (
        <motion.div
          key={clip.id}
          layout
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0, scale: 1, x: 0 }}
          exit={{ opacity: 0, scale: 0.95, x: -20, transition: { duration: 0.2 } }}
          transition={{ layout: { type: 'spring', stiffness: 350, damping: 30 } }}
        >
          <ClipCard
            clip={clip}
            position={index + 1}
            episodeStatus={episode.status}
            isFirst={index === 0}
            isLast={index === clips.length - 1}
            onReorder={(dir) => handleMoveClip(clip.id, dir)}
            onDelete={() => handleDeleteClip(clip.id)}
            onRegenerateScript={() => handleRegenerateScript(clip.id)}
            onRegenerateAudio={() => handleRegenerateAudio(clip.id)}
            onEditScript={(script) => saveScriptForClip(clip.id, script)}
            isPlaying={currentlyPlayingId === clip.id && !isPlayingAllClips}
            isActiveInPlayAll={isPlayingAllClips && playAllClipsIndex === index}
            isPlayAllPaused={isPlayAllClipsPaused}
            onPlayStateChange={handlePlayStateChange}
            onEnded={() => handleClipEnded(index)}
            actionInProgress={actionInProgress}
            isLocked={isLockedByOther}
          />
        </motion.div>
      ))}
    </AnimatePresence>
  </div>
);

// ─────────────────────────────────────────────────────────────
// Status-specific components
// ─────────────────────────────────────────────────────────────

const DraftContent: React.FC<EpisodeStatusContentProps> = (props) => {
  const { episode, clips, clipsLoading, onAddArticles } = props;

  if (clipsLoading && clips.length === 0) {
    return (
      <div className="border-2 border-dashed border-border rounded-lg p-24 flex items-center justify-center bg-podcast relative z-10">
        <div className="text-center relative z-20">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
          <h3 className="text-h2 mb-2">Adding clips...</h3>
          <p className="text-muted-foreground">Creating clips from selected articles</p>
        </div>
      </div>
    );
  }

  if (clips.length === 0) {
    if (episode.status === EpisodeStatus.ARCHIVED) {
      return (
        <div className="border-2 border-dashed border-border rounded-lg p-24 flex items-center justify-center bg-podcast">
          <div
            className="border border-border rounded-xl px-12 py-16 text-center"
            style={{
              background: 'var(--pm-gradient-dark)',
              boxShadow: '0 8px 32px var(--pm-shadow), inset 0 1px 0 var(--pm-surface-1), inset 0 -1px 0 var(--pm-shadow-strong)'
            }}
          >
            <Info className="h-12 w-12 mx-auto mb-6 text-muted-foreground" />
            <h3 className="text-h2 mb-3">This episode is archived</h3>
            <p className="text-muted-foreground">Unarchive to add articles</p>
          </div>
        </div>
      );
    }

    return (
      <div className="border-2 border-dashed border-border rounded-lg p-24 flex items-center justify-center bg-podcast">
        <style>{`
          @keyframes empty-state-fade-in {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .icon-shuffle {
            position: relative;
            width: 64px;
            height: 80px;
            margin-bottom: 8px;
          }
          .shuffle-page {
            position: absolute;
            width: 48px;
            height: 60px;
            background: var(--pm-bg-base);
            border: 2px solid var(--pm-text-faint);
            border-radius: 4px;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
          }
          .shuffle-page::before {
            content: '';
            position: absolute;
            top: 12px;
            left: 8px;
            right: 8px;
            height: 2px;
            background: var(--pm-text-faint);
            border-radius: 1px;
          }
          .shuffle-page::after {
            content: '';
            position: absolute;
            top: 20px;
            left: 8px;
            right: 12px;
            height: 2px;
            background: var(--pm-text-faint);
            border-radius: 1px;
          }
          .shuffle-page:nth-child(1) {
            z-index: 3;
            border-color: var(--pm-cyan);
            animation: shuffleFront 2.5s ease-in-out infinite;
          }
          .shuffle-page:nth-child(2) {
            z-index: 2;
            transform: translate(calc(-50% + 6px), calc(-50% + 4px));
            animation: shuffleMiddle 2.5s ease-in-out infinite;
          }
          .shuffle-page:nth-child(3) {
            z-index: 1;
            transform: translate(calc(-50% + 12px), calc(-50% + 8px));
            animation: shuffleBack 2.5s ease-in-out infinite;
          }
          @keyframes shuffleFront {
            0%, 100% { transform: translate(-50%, -50%) rotate(0deg); }
            20% { transform: translate(calc(-50% - 8px), -50%) rotate(-3deg); }
            40% { transform: translate(-50%, -50%) rotate(0deg); }
          }
          @keyframes shuffleMiddle {
            0%, 100% { transform: translate(calc(-50% + 6px), calc(-50% + 4px)) rotate(0deg); }
            20% { transform: translate(calc(-50% + 2px), calc(-50% + 4px)) rotate(-1deg); }
            40% { transform: translate(calc(-50% + 6px), calc(-50% + 4px)) rotate(0deg); }
            60% { transform: translate(calc(-50% + 10px), calc(-50% + 4px)) rotate(2deg); }
            80% { transform: translate(calc(-50% + 6px), calc(-50% + 4px)) rotate(0deg); }
          }
          @keyframes shuffleBack {
            0%, 100% { transform: translate(calc(-50% + 12px), calc(-50% + 8px)) rotate(0deg); }
            50% { transform: translate(calc(-50% + 16px), calc(-50% + 8px)) rotate(3deg); }
            70% { transform: translate(calc(-50% + 12px), calc(-50% + 8px)) rotate(0deg); }
          }
        `}</style>
        <div
          className="border border-border rounded-xl px-12 py-16 text-center"
          style={{
            background: 'var(--pm-gradient-dark)',
            boxShadow: '0 8px 32px var(--pm-shadow), inset 0 1px 0 var(--pm-surface-1), inset 0 -1px 0 var(--pm-shadow-strong)'
          }}
        >
          <div className="icon-shuffle mx-auto mb-6" style={{ animation: 'empty-state-fade-in 0.5s ease-out both' }}>
            <div className="shuffle-page"></div>
            <div className="shuffle-page"></div>
            <div className="shuffle-page"></div>
          </div>
          <h3 className="text-h2 mb-3" style={{ animation: 'empty-state-fade-in 0.5s ease-out 0.2s both' }}>
            No clips yet
          </h3>
          <p className="text-muted-foreground mb-6" style={{ animation: 'empty-state-fade-in 0.5s ease-out 0.4s both' }}>
            Add articles to build your episode
          </p>
          <Button onClick={onAddArticles}>
            <Plus className="h-4 w-4 mr-2" />
            Add Articles
          </Button>
        </div>
      </div>
    );
  }

  return <ClipList {...props} />;
};

const GeneratingScriptsContent: React.FC<EpisodeStatusContentProps> = ({ clips }) => {
  const scriptsReady = clips.filter((c) =>
    ['script_ready', 'script_approved', 'audio_queued', 'generating_audio', 'audio_ready', 'audio_approved'].includes(c.status)
  ).length;

  return (
    <ScriptGenerationAnimation
      completedClips={scriptsReady}
      totalClips={clips.length || 1}
    />
  );
};

const GeneratingAudioContent: React.FC<EpisodeStatusContentProps> = ({ clips }) => {
  const audioReady = clips.filter((c) =>
    ['audio_ready', 'audio_approved'].includes(c.status)
  ).length;

  return (
    <AudioGenerationAnimation
      completedClips={audioReady}
      totalClips={clips.length || 1}
    />
  );
};

const AudioApprovedContent: React.FC<EpisodeStatusContentProps> = (props) => {
  const {
    episode, clips, actionInProgress, refreshEpisode, loadClips,
    handleMoveClip, handleDeleteClip, handleRegenerateScript, handleRegenerateAudio, saveScriptForClip,
    currentlyPlayingId, isPlayingAll, playAllIndex, isPlayAllPaused,
    handlePlayAll, handleStopPlayAll, handlePlayStateChange, handleSegmentEnded,
    totalDuration, isLockedByOther,
  } = props;

  const mergeSequence = episode?.mergePreview ? buildMergeSequence(clips, episode.mergePreview) : [];
  const hasIntroOrOutro = episode?.mergePreview?.introPath || episode?.mergePreview?.outroPath;
  const hasCommercials = [
    episode?.mergePreview?.commercial1Path,
    episode?.mergePreview?.commercial2Path,
    episode?.mergePreview?.commercial3Path,
    episode?.mergePreview?.commercial4Path,
  ].some((p) => p?.trim());

  let clipNumber = 0;
  const totalClipsInSequence = mergeSequence.filter((item) => item.type === 'clip').length;

  return (
    <>
      <HeaderRow
        label="Episode Preview"
        count={mergeSequence.length}
        progress={totalDuration > 0 ? `Total: ~${formatTotalDuration(totalDuration)}` : undefined}
        onRefresh={() => { refreshEpisode(); loadClips(); }}
        actionInProgress={actionInProgress}
        actionButton={
          mergeSequence.length > 0 && (
            <Button variant="outline" size="sm" onClick={isPlayingAll ? handleStopPlayAll : handlePlayAll}>
              {isPlayingAll ? (
                <><Square className="h-4 w-4 mr-2" />Stop</>
              ) : (
                <><Play className="h-4 w-4 mr-2" />Play All</>
              )}
            </Button>
          )
        }
      />

      {mergeSequence.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <p>No audio clips ready for merge.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {mergeSequence.map((item, index) => {
            const sequenceKey = `${item.type}-${index}`;

            if (item.type === 'clip' && item.clip) {
              clipNumber++;
              return (
                <ClipCard
                  key={item.clip.id}
                  clip={item.clip}
                  position={clipNumber}
                  episodeStatus={episode.status}
                  isFirst={clipNumber === 1}
                  isLast={clipNumber === totalClipsInSequence}
                  onReorder={(dir) => handleMoveClip(item.clip!.id, dir)}
                  onDelete={() => handleDeleteClip(item.clip!.id)}
                  onRegenerateScript={() => handleRegenerateScript(item.clip!.id)}
                  onRegenerateAudio={() => handleRegenerateAudio(item.clip!.id)}
                  onEditScript={(script) => saveScriptForClip(item.clip!.id, script)}
                  isPlaying={currentlyPlayingId === item.clip.id}
                  isActiveInPlayAll={isPlayingAll && playAllIndex === index}
                  isPlayAllPaused={isPlayAllPaused}
                  onPlayStateChange={handlePlayStateChange}
                  onEnded={() => handleSegmentEnded(index)}
                  actionInProgress={actionInProgress}
                  isLocked={isLockedByOther}
                />
              );
            }

            const previewItemId = `${item.type}-${item.commercialIndex ?? 0}`;
            return (
              <MergePreviewItem
                key={sequenceKey}
                type={item.type as 'intro' | 'outro' | 'commercial'}
                label={item.label}
                audioPath={item.audioPath}
                companyId={episode?.mergePreview?.companyId}
                index={item.commercialIndex}
                duration={item.duration}
                isPlaying={currentlyPlayingId === previewItemId}
                isActiveInPlayAll={isPlayingAll && playAllIndex === index}
                isPlayAllPaused={isPlayAllPaused}
                onPlayStateChange={handlePlayStateChange}
                onEnded={() => handleSegmentEnded(index)}
              />
            );
          })}
        </div>
      )}

      {(hasIntroOrOutro || hasCommercials) && (
        <div className="mt-4 p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
          <p>
            This preview shows the exact order of audio that will be merged:
            {episode?.mergePreview?.introPath && ' intro →'}
            {' clips'}
            {hasCommercials && ' with commercials between them'}
            {episode?.mergePreview?.outroPath && ' → outro'}
            .
          </p>
        </div>
      )}
    </>
  );
};

const MergingContent: React.FC = () => (
  <div
    className="border border-border rounded-lg p-24 flex items-center justify-center"
    style={{ background: 'var(--pm-gradient-dark)' }}
  >
    <div className="text-center">
      <h3 className="text-h2 mb-2">Merging Episode</h3>
      <p className="text-muted-foreground mb-6">Combining all audio clips into final episode...</p>
      <MergingAnimation />
    </div>
  </div>
);

const MergedAudioPlayer: React.FC<{
  mergedAudioLoading: boolean;
  mergedAudioError: string | null;
  mergedAudioUrl: string | null;
  mergedAudioRef: React.RefObject<AudioPlayerHandle | null>;
  isPlayingMerged: boolean;
  setIsPlayingMerged: (playing: boolean) => void;
  mergedAudioPath?: string | null;
}> = ({ mergedAudioLoading, mergedAudioError, mergedAudioUrl, mergedAudioRef, isPlayingMerged, setIsPlayingMerged, mergedAudioPath }) => {
  if (mergedAudioLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading audio player...
      </div>
    );
  }
  if (mergedAudioError) return <div className="text-sm text-destructive">{mergedAudioError}</div>;
  if (mergedAudioUrl) {
    return (
      <AudioPlayer
        ref={mergedAudioRef}
        src={mergedAudioUrl}
        isPlayingExternal={isPlayingMerged}
        onPlay={() => setIsPlayingMerged(true)}
        onPause={() => setIsPlayingMerged(false)}
        onEnded={() => setIsPlayingMerged(false)}
      />
    );
  }
  if (mergedAudioPath) return <div className="text-sm text-muted-foreground">Audio path: {mergedAudioPath}</div>;
  return null;
};

const MergedContent: React.FC<EpisodeStatusContentProps> = ({
  episode, clips,
  mergedAudioLoading, mergedAudioError, mergedAudioUrl, mergedAudioRef,
  isPlayingMerged, setIsPlayingMerged,
}) => (
  <>
    <div className="p-4 bg-podcast rounded-lg mb-6 space-y-4 border border-border">
      <div className="flex items-center gap-4">
        <FileAudio className="h-8 w-8 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{episode.title}</p>
          <p className="text-sm text-muted-foreground truncate">
            {episode.mergedAudioPath || episode.audioUrl || 'Audio file ready'}
          </p>
        </div>
        <Badge variant="outline-success" className="shrink-0">
          <CheckCircle className="h-3 w-3 mr-1" />
          Ready
        </Badge>
      </div>
      <MergedAudioPlayer
        mergedAudioLoading={mergedAudioLoading}
        mergedAudioError={mergedAudioError}
        mergedAudioUrl={mergedAudioUrl}
        mergedAudioRef={mergedAudioRef}
        isPlayingMerged={isPlayingMerged}
        setIsPlayingMerged={setIsPlayingMerged}
        mergedAudioPath={episode.mergedAudioPath}
      />
      <PublishedClipsList clips={clips} />
    </div>
  </>
);

const PublishingContent: React.FC = () => (
  <div
    className="border border-border rounded-lg p-24 flex items-center justify-center"
    style={{ background: 'var(--pm-gradient-dark)' }}
  >
    <div className="text-center">
      <h3 className="text-h2 mb-2">Publishing to Podbean</h3>
      <p className="text-muted-foreground mb-6">Uploading your episode...</p>
      <PublishingAnimation />
    </div>
  </div>
);

const PublishedContent: React.FC<EpisodeStatusContentProps> = ({
  episode, clips,
  mergedAudioLoading, mergedAudioError, mergedAudioUrl, mergedAudioRef,
  isPlayingMerged, setIsPlayingMerged,
}) => (
  <>
    <div className="p-4 bg-podcast rounded-lg mb-6 space-y-4 border border-border">
      <div className="flex items-center gap-4">
        <FileAudio className="h-8 w-8 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{episode.title}</p>
          <p className="text-sm text-muted-foreground">
            {(() => {
              const scheduledDate = toDate(episode.scheduledPublishDate);
              const publishedDate = toDate(episode.publishedAt);
              if (scheduledDate && scheduledDate > new Date()) {
                return `Scheduled for ${format(scheduledDate, 'MMMM d, yyyy \'at\' h:mm a')}`;
              }
              if (publishedDate) {
                return `Published ${format(publishedDate, 'MMMM d, yyyy \'at\' h:mm a')}`;
              }
              return 'Successfully published';
            })()}
          </p>
        </div>
        <StatusBadge status={EpisodeStatus.PUBLISHED} scheduledPublishDate={episode.scheduledPublishDate} />
      </div>
      <MergedAudioPlayer
        mergedAudioLoading={mergedAudioLoading}
        mergedAudioError={mergedAudioError}
        mergedAudioUrl={mergedAudioUrl}
        mergedAudioRef={mergedAudioRef}
        isPlayingMerged={isPlayingMerged}
        setIsPlayingMerged={setIsPlayingMerged}
      />
      <PublishedClipsList clips={clips} />
    </div>

    {episode.publishingProvider && (
      <div className="pt-4 border-t">
        <h4 className="font-medium mb-2">Publishing Details</h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Provider:</span>
            <span className="ml-2 font-medium capitalize">{episode.publishingProvider}</span>
          </div>
          {episode.publishingProviderId && (
            <div>
              <span className="text-muted-foreground">Episode ID:</span>
              <span className="ml-2 font-mono text-xs">{episode.publishingProviderId}</span>
            </div>
          )}
        </div>
      </div>
    )}
  </>
);

const PublishingFailedContent: React.FC<EpisodeStatusContentProps> = ({ episode }) => (
  <>
    <Alert variant="destructive" className="mb-6">
      <AlertCircle className="h-4 w-4" />
      <AlertDescription>
        <div className="flex items-center justify-between">
          <div>
            <strong>Publishing failed.</strong>
            {episode.errorMessage && ` ${episode.errorMessage}`}
          </div>
        </div>
      </AlertDescription>
    </Alert>

    <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
      <FileAudio className="h-8 w-8 text-primary" />
      <div className="flex-1">
        <p className="font-medium">{episode.title}</p>
        <p className="text-sm text-muted-foreground">
          {episode.mergedAudioPath || episode.audioUrl || 'Audio file ready'}
        </p>
      </div>
    </div>
  </>
);
